import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Pause, Play, X } from 'lucide-react';
import { MasteryLevelSelector } from '@/components/molecules/mastery-level-selector';
import { formatMmSsFromMs } from '@/lib/format-time';
import { usePlayback } from '@/lib/hooks/use-playback';
import { cn } from '@/lib/cn';
import type { Take } from '@/lib/hooks/use-recorder';

export type TakeReviewLabel = 'scratch' | 'in-progress' | 'polished' | 'reference';

type LabelOption = {
  value: TakeReviewLabel;
  display: string;
};

const LABEL_OPTIONS: readonly LabelOption[] = [
  { value: 'scratch', display: 'Scratch' },
  { value: 'in-progress', display: 'In progress' },
  { value: 'polished', display: 'Polished' },
  { value: 'reference', display: 'Reference' },
];

const DEFAULT_LABEL: TakeReviewLabel = 'in-progress';

export type TakeReviewSavePayload = {
  label: TakeReviewLabel;
  masterySortOrder: number | null;
  notes: string;
};

export type TakeReviewSurfaceProps = {
  open: boolean;
  take: Take | null;
  onSave: (payload: TakeReviewSavePayload) => void;
  onDiscard: () => void;
  onRetake: () => void;
  onClose: () => void;
};

export function TakeReviewSurface({
  open,
  take,
  onSave,
  onDiscard,
  onRetake,
  onClose,
}: TakeReviewSurfaceProps): ReactElement {
  const [label, setLabel] = useState<TakeReviewLabel>(DEFAULT_LABEL);
  const [masterySortOrder, setMasterySortOrder] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');

  const audioRef = useRef<HTMLAudioElement>(null);
  const playback = usePlayback(audioRef);
  const { load } = playback;
  const blob = take?.blob ?? null;

  useEffect(() => {
    if (!open || !blob) return;
    load(blob);
  }, [open, blob, load]);

  const isPlaying = playback.status === 'playing';
  const playbackDurationMs = take ? playback.durationMs || take.durationMs : 0;

  const handleTogglePlayback = (): void => {
    if (isPlaying) {
      playback.pause();
    } else {
      void playback.play();
    }
  };

  const handleSave = (): void => {
    onSave({ label, masterySortOrder, notes });
  };

  // The audio element must stay mounted at a stable tree position whenever this
  // component renders — not only when the dialog is open. usePlayback attaches
  // its listeners once, on first mount; if the element appeared (or moved) on
  // the open transition the listeners would never bind and playback state would
  // never update. The real practice flow mounts the surface closed and opens it
  // later, so the element lives outside the open/take guard, ahead of the dialog.
  return (
    <>
      <audio ref={audioRef} aria-label="take-audio" preload="metadata" className="hidden" />
      {open && take && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Review take"
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
        >
          <button
            type="button"
            aria-label="Dismiss review by clicking outside"
            onClick={onClose}
            className="absolute inset-0 bg-black/50"
          />
          <div
            className={cn(
              'relative flex max-h-[90vh] w-full flex-col overflow-hidden border border-[var(--rule)]',
              'rounded-t-3xl bg-[var(--bg-surface)] shadow-2xl',
              'sm:max-w-2xl sm:rounded-3xl',
            )}
          >
            <div className="flex items-center justify-between border-b border-[var(--rule)] px-6 py-4">
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
                Review take · {formatMmSsFromMs(take.durationMs)}
              </h2>
              <button
                type="button"
                aria-label="Close review"
                onClick={onClose}
                className="rounded-full p-1 text-[var(--ink-3)] hover:bg-[var(--bg-soft)] hover:text-[var(--ink)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
              <div className="flex items-center gap-4 rounded-2xl border border-[var(--rule)] bg-[var(--bg-soft)] px-4 py-3">
                <button
                  type="button"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={handleTogglePlayback}
                  className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-lg hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5 fill-current" />
                  ) : (
                    <Play className="h-5 w-5 fill-current" />
                  )}
                </button>
                <span className="font-mono text-xs tabular-nums text-[var(--ink-2)]">
                  {formatMmSsFromMs(playback.currentMs)} / {formatMmSsFromMs(playbackDurationMs)}
                </span>
              </div>
              <div>
                <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
                  Label
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {LABEL_OPTIONS.map((option) => {
                    const isSelected = option.value === label;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => setLabel(option.value)}
                        className={cn(
                          'flex h-14 items-center justify-center rounded-xl border text-xs font-bold transition-all',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                          isSelected
                            ? 'border-[var(--brand)] bg-[var(--brand)] text-white'
                            : 'border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] hover:border-[var(--brand)]',
                        )}
                      >
                        {option.display}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]">
                  Mastery rating
                </p>
                <MasteryLevelSelector value={masterySortOrder} onChange={setMasterySortOrder} />
              </div>

              <div>
                <label
                  htmlFor="take-notes"
                  className="mb-3 block text-[10px] font-bold uppercase tracking-widest text-[var(--ink-3)]"
                >
                  Notes
                </label>
                <textarea
                  id="take-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  placeholder="What went well? What to revisit?"
                  className={cn(
                    'w-full rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)]',
                    'px-3 py-2 text-sm text-[var(--ink)]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]',
                  )}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-[var(--rule)] px-6 py-4">
              <button
                type="button"
                onClick={onDiscard}
                className="inline-flex h-10 items-center justify-center rounded-[var(--r-sm)] px-4 text-sm font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={onRetake}
                className="inline-flex h-10 items-center justify-center rounded-[var(--r-sm)] px-4 text-sm font-semibold bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]"
              >
                Retake
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex h-10 items-center justify-center rounded-[var(--r-sm)] px-4 text-sm font-semibold bg-[var(--ink)] text-[var(--bg-surface)] hover:bg-[var(--brand-ink)]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
